import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TesterRole, type IdentitySwitchRequest, type UserProfile } from '@regigov/contracts';
import { testingApi } from '../api/testing';
import { useAuth } from '../auth/AuthContext';
import { Field, ProblemAlert } from './ui';

const ROLE_LABELS: Record<TesterRole, string> = {
  cer_admin: 'CER admin',
  cer_officer: 'CER officer',
  participant_admin: 'Participant admin',
  participant_user: 'Participant user',
  applicant: 'Applicant',
};
const NEW_APPLICATION = '';

/** TESTING_MODE, Tester accounts only: the username in the top bar opens a popover to switch role and organisation (docs/testing-mode.md). */
export function IdentitySwitcher({ user }: { user: UserProfile }) {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const current = TesterRole.safeParse(user.roles[0]);
  const [role, setRole] = useState<TesterRole>(current.success ? current.data : 'cer_officer');
  const [filter, setFilter] = useState('');
  const [participantId, setParticipantId] = useState(user.participantId ?? '');
  const [applicationId, setApplicationId] = useState(NEW_APPLICATION);
  const [heldChoice, setHeldChoice] = useState<'continue' | 'new'>('continue');
  const options = useQuery({ queryKey: ['testing', 'options'], queryFn: testingApi.options, enabled: open, staleTime: 0, gcTime: 0 });
  const switcher = useMutation({
    mutationFn: testingApi.switchIdentity,
    onSuccess: (res) => {
      adopt(res);
      navigate(`/${res.user.workbench}`);
    },
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const participantRole = role === 'participant_admin' || role === 'participant_user';
  const held = options.data?.held ?? null;
  const needle = filter.trim().toLowerCase();
  const organisations = (options.data?.organisations ?? []).filter((o) => !needle || o.legalName.toLowerCase().includes(needle) || o.abn.includes(needle) || o.id.toLowerCase().includes(needle));
  const chosenVisible = organisations.some((o) => o.id === participantId);
  const request: IdentitySwitchRequest | null = participantRole
    ? chosenVisible
      ? { role, participantId }
      : null
    : role === 'applicant'
      ? held
        ? heldChoice === 'continue'
          ? { role, applicationId: held.id }
          : { role, withdrawHeld: true }
        : applicationId
          ? { role, applicationId }
          : { role }
      : { role };

  return (
    <div className="identity-switcher" ref={ref} onMouseEnter={() => setOpen(true)}>
      <button type="button" className="identity-switcher__trigger topbar__user" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        {user.displayName} <span className="muted">({user.roles.join(', ')})</span> ▾
      </button>
      {open && (
        <div className="identity-switcher__panel" role="dialog" aria-label="Switch role and organisation">
          <h2>Switch role (Tester)</h2>
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value as TesterRole)}>
              {TesterRole.options.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </Field>
          {participantRole && (
            <>
              <Field label="Organisation">
                <input type="search" placeholder="Filter by name, ABN or ID" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </Field>
              <select aria-label="Organisation" size={6} value={participantId} onChange={(e) => setParticipantId(e.target.value)}>
                <option value="" disabled>
                  Choose an organisation
                </option>
                {organisations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.legalName} ({o.abn})
                  </option>
                ))}
              </select>
              {options.data && organisations.length === 0 && <p className="muted">No approved organisation matches.</p>}
            </>
          )}
          {role === 'applicant' && held && (
            <fieldset className="identity-switcher__held">
              <legend>You hold the open application for {held.legalName}</legend>
              <label className="check">
                <input type="radio" name="held" checked={heldChoice === 'continue'} onChange={() => setHeldChoice('continue')} />
                Continue it ({held.status.replace(/_/g, ' ')})
              </label>
              <label className="check">
                <input type="radio" name="held" checked={heldChoice === 'new'} onChange={() => setHeldChoice('new')} />
                Begin a new application (withdraws {held.legalName})
              </label>
            </fieldset>
          )}
          {role === 'applicant' && options.data && !held && (
            <Field label="Application" hint="Taking over an application moves it to you; its current applicant loses access.">
              <select value={applicationId} onChange={(e) => setApplicationId(e.target.value)}>
                <option value={NEW_APPLICATION}>Start a new application</option>
                {(options.data?.applications ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    Take over: {a.legalName} ({a.status.replace(/_/g, ' ')}, {a.applicantName})
                  </option>
                ))}
              </select>
            </Field>
          )}
          <ProblemAlert error={options.error ?? switcher.error} />
          <button type="button" disabled={!request || switcher.isPending} onClick={() => request && switcher.mutate(request)}>
            {switcher.isPending ? 'Switching…' : 'Switch'}
          </button>
        </div>
      )}
    </div>
  );
}
